/**
 * HTML escaping for the one place this app builds markup by hand: Leaflet tooltips.
 *
 * Leaflet's `bindTooltip(content)` assigns a string straight to `innerHTML` — that is the
 * documented behaviour, and it is what lets a tooltip carry `<b>` and a coloured `<span>`.
 * Every tooltip in `features/map/` is therefore a template literal, and four of them
 * interpolate **server text**: unit names and layer names from `/hierarchy/units`,
 * `/layers` and `/layers/{id}/features`.
 *
 * Those strings are trusted today — they come from the IFDC boundary pack and the §C1 seed
 * — but "trusted" is a property of the current data, not of the code. §G's upload pipeline
 * (M6) puts dataset titles and unit names in the hands of anyone with an analyst account,
 * and a `sites.name` reaching `innerHTML` unescaped is then stored XSS with a
 * hover as the trigger. Escaping at the interpolation costs a function call and removes
 * the class of bug rather than the current instance of it.
 *
 * **Escape the values, never the template.** The markup around them is ours and is meant
 * to be markup; only the parts that came from outside are escaped. That is why this is a
 * bare function rather than a tagged template: at every call site it should be visible
 * which fragment is data.
 *
 * The five characters are the standard OWASP set. `&` must be first, or the ampersands
 * introduced by the later replacements would be double-escaped.
 */

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * `escapeHtml('Ségou & "Kou" <b>')` → `'Ségou &amp; &quot;Kou&quot; &lt;b&gt;'`.
 *
 * Non-strings are coerced (`null`/`undefined` → `''`, not the strings "null"/"undefined"):
 * a tooltip built from a missing property should be empty, not a word the reader will
 * take for data.
 */
export function escapeHtml(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[&<>"']/g, (ch) => ESCAPES[ch] ?? ch);
}
